import React, { useEffect, useState } from "react"
import { Icon } from "components"
import Strings from "utils/strings"
import { API, Endpoints } from "utils/api"
import { FleetVehicle, VehicleJourneySegment } from "../types"
import { formatDistance, formatTimeWindow, getTaskForStop } from "../utils"
import { FLEET_MAP_COLORS } from "utils/fleetMapConfig"

interface VehiclePanelProps {
	vehicle: FleetVehicle
	tasks: VehicleJourneySegment[]
	onClose: () => void
	isFullscreen?: boolean
}

const DEFAULT_STOP = {
	dot: "#d9d9d9",
	bg: "#fafafa",
	border: "#f0f0f0",
	label: "",
}

const VehiclePanel: React.FC<VehiclePanelProps> = ({
	vehicle,
	tasks,
	onClose,
	isFullscreen,
}) => {
	const STOP_STYLES: Record<
		string,
		{ dot: string; bg: string; border: string; label: string }
	> = {
		ENROUTE: {
			dot: FLEET_MAP_COLORS.markerEnroute,
			bg: "#e6f4ff",
			border: "#bae0ff",
			label: Strings.routes.fleetTracking.legendEnroute,
		},
		ARRIVED: {
			dot: FLEET_MAP_COLORS.markerArrived,
			bg: "#fffbe6",
			border: "#ffe58f",
			label: Strings.routes.fleetTracking.legendArrived,
		},
	}
	const [orderMap, setOrderMap] = useState<Record<string, string>>({})
	const hasFetchedRef = React.useRef(false)

	useEffect(() => {
		if (!tasks?.length || hasFetchedRef.current) return

		const ids = tasks.flatMap(
			(seg) => seg.stop?.tasks?.map((t: any) => t.id) || []
		)
		if (!ids.length) return

		hasFetchedRef.current = true
		API.post({
			url: Endpoints.uriAnalytics(),
			data: { fleetTaskIds: ids },
		}).then((res: any) => {
			if (res.ok) {
				const map: Record<string, string> = {}
				;(res.data?.results?.analytics?.fleetTasks || []).forEach(
					(ft: { fleetTaskId: string; orderId: string }) => {
						map[ft.fleetTaskId] = ft.orderId
					}
				)
				setOrderMap(map)
			}
		})
	}, [vehicle.remainingVehicleJourneySegments])

	const plate = vehicle.licensePlate || vehicle.vehicleId
	const segments = vehicle.remainingVehicleJourneySegments || []

	const stats = [
		{
			icon: "route",
			value: formatDistance(vehicle.remainingDistanceMeters),
			label: Strings.routes.fleetTracking.remainingDistance,
			color: "#075aae",
		},
		...(vehicle.speed !== undefined
			? [
					{
						icon: "velocimetro",
						value: `${Math.round(vehicle.speed)} km/h`,
						label: Strings.routes.fleetTracking.speed,
						color: "#e67e22",
					},
				]
			: []),
		...(vehicle.lastUpdate
			? [
					{
						icon: "clock1",
						value: vehicle.lastUpdate.toLocaleTimeString("pt-PT", {
							hour: "2-digit",
							minute: "2-digit",
						}),
						label: Strings.routes.fleetTracking.lastUpdate,
						color: "#163683",
					},
				]
			: []),
	]

	return (
		<div
			style={{
				...(isFullscreen
					? {
							position: "absolute",
							top: 10,
							left: 10,
							zIndex: 100,
							width: 340,
							height: "calc(100% - 20px)",
							borderRadius: 8,
							boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
						}
					: {
							width: "100%",
							height: "100%",
						}),
				display: "flex",
				flexDirection: "column",
				backgroundColor: "#fff",
				overflow: "hidden",
			}}
		>
			{/* Header */}
			<div
				style={{
					background: "linear-gradient(135deg, #075aae 0%, #1a7fd4 100%)",
					padding: "20px",
					flexShrink: 0,
				}}
			>
				<div
					style={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
					}}
				>
					<div style={{ display: "flex", alignItems: "center", gap: 12 }}>
						<div
							style={{
								width: 44,
								height: 44,
								borderRadius: "50%",
								backgroundColor: "rgba(255,255,255,0.2)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
							}}
						>
							<Icon
								name="delivery-truck"
								style={{ fontSize: 22, color: "white" }}
							/>
						</div>
						<div>
							<div style={{ fontSize: 17, fontWeight: 700, color: "white" }}>
								{plate}
							</div>
							<div
								style={{
									fontSize: 12,
									color: "rgba(255,255,255,0.7)",
									marginTop: 3,
								}}
							>
								{segments.length > 0
									? `${segments.length} ${Strings.fleetDashboard.stops.toLowerCase()} restantes`
									: Strings.fleetDashboard.noStops}
							</div>
						</div>
					</div>
					<button
						onClick={onClose}
						style={{
							background: "rgba(255,255,255,0.18)",
							border: "none",
							borderRadius: "50%",
							width: 30,
							height: 30,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							cursor: "pointer",
							flexShrink: 0,
						}}
					>
						<Icon name="close" style={{ fontSize: 12, color: "white" }} />
					</button>
				</div>
			</div>

			{/* Stats row */}
			{stats.length > 0 && (
				<div
					style={{
						display: "flex",
						flexShrink: 0,
						borderBottom: "1px solid #f0f0f0",
					}}
				>
					{stats.map((stat, i) => (
						<div
							key={i}
							style={{
								flex: 1,
								padding: "14px 8px",
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 4,
								borderRight:
									i < stats.length - 1 ? "1px solid #f0f0f0" : "none",
							}}
						>
							<Icon
								name={stat.icon}
								style={{ fontSize: 16, color: stat.color }}
							/>
							<span style={{ fontSize: 13, fontWeight: 700, color: "#103246" }}>
								{stat.value}
							</span>
							<span
								style={{ fontSize: 10, color: "#8e9aab", textAlign: "center" }}
							>
								{stat.label}
							</span>
						</div>
					))}
				</div>
			)}

			{/* Stops */}
			<div
				style={{ flex: 1, overflowY: "auto", padding: "16px 16px 16px 16px" }}
			>
				{segments.length === 0 ? (
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
							gap: 12,
							color: "#8e9aab",
						}}
					>
						<Icon name="location" style={{ fontSize: 32, color: "#d9d9d9" }} />
						<span style={{ fontSize: 13 }}>
							{Strings.fleetDashboard.noStops}
						</span>
					</div>
				) : (
					<>
						<div
							style={{
								fontSize: 11,
								fontWeight: 700,
								color: "#8e9aab",
								textTransform: "uppercase",
								letterSpacing: "0.6px",
								marginBottom: 12,
							}}
						>
							{Strings.fleetDashboard.stops} ({segments.length})
						</div>

						{segments.map((segment, index) => {
							const stopTasks = segment.stop?.tasks || []
							const tasksWithDetails = stopTasks.map((task: any) => ({
								...task,
								details: getTaskForStop(tasks, task.id),
							}))
							const firstName =
								tasksWithDetails[0]?.details?.attributes?.clientName

							const state = segment.stop?.state || ""
							const s = STOP_STYLES[state] || DEFAULT_STOP
							const isLast = index === segments.length - 1

							return (
								<div
									key={index}
									style={{ display: "flex", gap: 0, position: "relative" }}
								>
									{/* Timeline column */}
									<div
										style={{
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											width: 32,
											flexShrink: 0,
										}}
									>
										<div
											style={{
												width: 26,
												height: 26,
												borderRadius: "50%",
												backgroundColor: s.dot,
												color: "white",
												fontSize: 11,
												fontWeight: 700,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												flexShrink: 0,
												zIndex: 1,
												boxShadow: `0 0 0 3px white`,
											}}
										>
											{index + 1}
										</div>
										{!isLast && (
											<div
												style={{
													width: 2,
													flex: 1,
													minHeight: 16,
													backgroundColor: "#e8e8e8",
													margin: "4px 0",
												}}
											/>
										)}
									</div>

									{/* Content */}
									<div
										style={{
											flex: 1,
											paddingLeft: 10,
											paddingBottom: isLast ? 0 : 16,
										}}
									>
										<div
											style={{
												backgroundColor: s.bg,
												border: `1px solid ${s.border}`,
												borderRadius: 8,
												padding: "10px 12px",
											}}
										>
											{/* Name + state badge */}
											<div
												style={{
													display: "flex",
													alignItems: "center",
													justifyContent: "space-between",
													gap: 6,
													marginBottom: 6,
												}}
											>
												<span
													style={{
														fontWeight: 600,
														fontSize: 13,
														color: "#103246",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{firstName ||
														`${Strings.fleetDashboard.stop} ${index + 1}`}
												</span>
												{s.label && (
													<span
														style={{
															fontSize: 10,
															fontWeight: 700,
															color: s.dot,
															backgroundColor: "white",
															border: `1px solid ${s.border}`,
															borderRadius: 4,
															padding: "2px 6px",
															flexShrink: 0,
														}}
													>
														{s.label}
													</span>
												)}
											</div>

											{/* Order tags */}
											{tasksWithDetails.some(
												(t: any) =>
													orderMap[t.id] ||
													t?.trackingId ||
													t.details?.attributes?.orderId
											) && (
												<div
													style={{
														display: "flex",
														flexWrap: "wrap",
														gap: 4,
														marginBottom: 8,
													}}
												>
													{tasksWithDetails.map((task: any, ti: number) => {
														const trackingId =
															orderMap[task.id] ||
															task?.trackingId ||
															task.details?.attributes?.orderId
														if (!trackingId) return null
														return (
															<span
																key={ti}
																style={{
																	display: "inline-flex",
																	alignItems: "center",
																	gap: 3,
																	padding: "2px 7px",
																	backgroundColor: "rgba(7,90,174,0.08)",
																	borderRadius: 4,
																	fontSize: 11,
																	fontWeight: 600,
																	color: "#075aae",
																}}
															>
																<Icon
																	name="pricetag-outline"
																	style={{ fontSize: 10 }}
																/>
																{trackingId}
															</span>
														)
													})}
												</div>
											)}

											{/* Meta info */}
											<div
												style={{
													display: "flex",
													flexWrap: "wrap",
													gap: 10,
													fontSize: 11,
													color: "#8e9aab",
												}}
											>
												{segment.stop?.tasks?.[0]?.targetTimeWindow && (
													<span
														style={{
															display: "flex",
															alignItems: "center",
															gap: 3,
														}}
													>
														<Icon
															name="timetable-outline"
															style={{ fontSize: 11, color: "#faad14" }}
														/>
														{formatTimeWindow(
															segment.stop.tasks[0].targetTimeWindow.startTime,
															segment.stop.tasks[0].targetTimeWindow.endTime
														)}
													</span>
												)}
												{segment.drivingDistanceMeters && (
													<span
														style={{
															display: "flex",
															alignItems: "center",
															gap: 3,
														}}
													>
														<Icon
															name="route"
															style={{ fontSize: 11, color: "#075aae" }}
														/>
														{formatDistance(segment.drivingDistanceMeters)}
													</span>
												)}
												{stopTasks.length > 1 && (
													<span
														style={{
															display: "flex",
															alignItems: "center",
															gap: 3,
														}}
													>
														<Icon
															name="package"
															style={{ fontSize: 11, color: "#722ed1" }}
														/>
														{stopTasks.length} entregas
													</span>
												)}
											</div>
										</div>
									</div>
								</div>
							)
						})}
					</>
				)}
			</div>
		</div>
	)
}

export default VehiclePanel
